/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, useTheme, useMediaQuery, Divider } from '@mui/material';
import React from 'react';
import PlaceholderImage from '../../../assets/images/image-placeholder.png';
import { ItemContainer, ItemLabel } from '../../../components/styled';
import { SalesLine } from '../storage/types';
import TestIds from '../../../testIds';
import { ProductHeaderDetails } from '../../products/components/ProductCard/ProductHeaderDetails';
import PrintVariants from '../../products/components/ProductCard/PrintVariants';
import StatusChip from '../../../components/StatusChip';
import { mapLineStatus } from '../utils/getOrderStatus';
import { knownDimensionNames } from '../../products/utils/useVariations';
import { formatNumber } from '../../../util/helpers';
import {
  ImageContainer,
  ImageWrapper,
  ProductCardContainer,
  ProductCardContent,
  TopDownWrapper,
} from '../../products/components/ProductCard/ProductCardParent';
import { VariantsContainer } from '../../products/components/ProductCard/ProductVariantDetails';
import { getOrderPrices } from '../../products/utils/getPrices';
import Card, { CardAction, CardTitle } from '../../../components/Card';
import { colors } from '@farmlands/mui-theme';

interface ProductCardProps {
  deliveryMode: string;
  createdAt: Date;
  item: SalesLine;
  isStatusVisible: boolean;
}

const OrderedProductCard = ({
  item,
  deliveryMode,
  createdAt,
  isStatusVisible,
}: ProductCardProps) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const variants = React.useMemo(() => {
    const results: { label: string; value: string }[] = [];
    item.productDimensions?.forEach((x) =>
      results.push({ label: `${knownDimensionNames[x.type]}:`, value: x.value }),
    );
    return results;
  }, [item]);

  const statusBadge = React.useMemo(() => {
    const lineStatus = mapLineStatus(item.detailedLineStatus, deliveryMode, createdAt);
    if (lineStatus && isStatusVisible) {
      return (
        <Box>
          <StatusChip
            label={lineStatus.display}
            variant='filled'
            bgColor={lineStatus.color.bg}
            textColor={lineStatus.color.text}
            data-testid={TestIds.feat.product.card.chip}
          />
        </Box>
      );
    }
  }, [item.detailedLineStatus, deliveryMode, createdAt]);

  return (
    <ProductCardContainer>
      <Card
        data-testid={TestIds.feat.product.card.card}
        data-id={item.productId}
        variant='outlined'
        disableLink
      >
        <ProductCardContent>
          <ImageWrapper>
            <ImageContainer
              src={item.imageUrl}
              placeholderImageUrl={PlaceholderImage}
              data-testid={TestIds.feat.product.card.image}
              alt=''
            />
          </ImageWrapper>
          <TopDownWrapper>
            <CardAction tabIndex={-1} disableRipple sx={{ p: 0 }} disabled>
              <CardTitle
                title={item.description}
                testId={TestIds.feat.product.card.name}
                showIcon={false}
              />
              <ProductHeaderDetails
                items={getOrderPrices({ item, deliveryMode, createdAt })}
                itemNumber={item.itemId}
                noPriceAvailable={false}
                orderStatusBadge={statusBadge}
              />
              <Divider sx={{ width: '100%', borderColor: colors.primary100 }} />
              <VariantsContainer>
                {item.productDimensions && <PrintVariants items={variants} />}
                <ItemContainer wrap={isSmall}>
                  <ItemLabel variant='body2'>Quantity:</ItemLabel>
                  <ItemLabel variant='body2' data-testid={TestIds.feat.product.card.quantity}>
                    {formatNumber(item.quantity)}
                  </ItemLabel>
                </ItemContainer>
              </VariantsContainer>
            </CardAction>
          </TopDownWrapper>
        </ProductCardContent>
      </Card>
    </ProductCardContainer>
  );
};

export default OrderedProductCard;
